import React from 'react';
import { Download, Play, Smartphone, BarChart3, FileText, Clock } from 'lucide-react';

const Hero = () => {
  return (
    <section className="bg-gradient-to-br from-primary-50 via-white to-primary-50 py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Transform Your{' '}
                <span className="bg-gradient-to-r from-primary-500 to-primary-600 bg-clip-text text-transparent">
                  Calling Sessions
                </span>{' '}
                with Professional Lead Management
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                The essential tool for sales teams, call centers, and business professionals. 
                Manage hundreds of leads systematically with automated calling and comprehensive tracking.
              </p>
            </div>

            {/* Key Benefits */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-success rounded-full flex items-center justify-center">
                  <Clock className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-medium text-gray-700">Time Efficient</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-success rounded-full flex items-center justify-center">
                  <BarChart3 className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-medium text-gray-700">Improved Productivity</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-success rounded-full flex items-center justify-center">
                  <FileText className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-medium text-gray-700">Professional Reports</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button className="bg-gradient-to-r from-primary-500 to-primary-600 text-white px-8 py-4 rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all duration-200 flex items-center justify-center space-x-2 text-lg font-semibold shadow-lg hover:shadow-xl">
                <Download className="w-5 h-5" />
                <span>Download Free App</span>
              </button>
              <button className="border-2 border-primary-500 text-primary-600 px-8 py-4 rounded-lg hover:bg-primary-500 hover:text-white transition-all duration-200 flex items-center justify-center space-x-2 text-lg font-semibold">
                <Play className="w-5 h-5" />
                <span>Watch Demo</span>
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="flex items-center space-x-6 text-sm text-gray-500">
              <div className="flex items-center space-x-1">
                <span>✓ Free to Use</span>
              </div>
              <div className="flex items-center space-x-1">
                <span>✓ Professional Tool</span>
              </div>
              <div className="flex items-center space-x-1">
                <span>✓ Easy to Use</span>
              </div>
            </div>
          </div>

          {/* Right Column - Phone Mockup */}
          <div className="relative">
            <div className="relative mx-auto w-80 h-[600px] bg-gray-900 rounded-[3rem] p-2 shadow-2xl">
              <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden relative">
                {/* Status Bar */}
                <div className="bg-primary-500 h-12 flex items-center justify-center">
                  <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                    <Smartphone className="w-4 h-4 text-primary-500" />
                  </div>
                </div>
                
                {/* App Content */}
                <div className="p-6 space-y-4">
                  <div className="text-center">
                    <h3 className="text-lg font-bold text-gray-900">Bulk Leads Caller</h3>
                    <p className="text-sm text-gray-600">Professional Calling Made Easy</p>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Progress</span>
                      <span className="font-semibold text-primary-600">75%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-gradient-to-r from-primary-500 to-primary-600 h-2 rounded-full" style={{width: '75%'}}></div>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-green-50 p-3 rounded-lg text-center">
                      <div className="text-lg font-bold text-green-600">✓</div>
                      <div className="text-xs text-green-600">Completed</div>
                    </div>
                    <div className="bg-orange-50 p-3 rounded-lg text-center">
                      <div className="text-lg font-bold text-orange-600">⏳</div>
                      <div className="text-xs text-orange-600">Pending</div>
                    </div>
                  </div>

                  {/* Call Button */}
                  <button className="w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white py-3 rounded-lg font-semibold flex items-center justify-center space-x-2">
                    <Smartphone className="w-5 h-5" />
                    <span>Call Next Lead</span>
                  </button>
                </div>
              </div>
            </div>
            
            {/* Floating Elements */}
            <div className="absolute -top-4 -right-4 w-16 h-16 bg-success rounded-full flex items-center justify-center shadow-lg animate-bounce">
              <span className="text-white font-bold text-lg">✓</span>
            </div>
            <div className="absolute -bottom-4 -left-4 w-12 h-12 bg-warning rounded-full flex items-center justify-center shadow-lg animate-pulse">
              <span className="text-white font-bold">📊</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
